import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { Exchange } from '../../../../types/exchange';
import { GetTopAssetsByVolumeInfo } from '../../../../types/Bot';
import { Subscription } from '../../../../types/subscription';
import { useSelector } from 'react-redux';
import { selectBaskets } from '../../../../store/slices/subscription';

type BotItemContext = {
  possibleQuoteAssets: string[];
  selectedQuoteAsset: string;
  handleSelectedQuoteAsset: (quoteAsset: string) => void;
  possibleExchanges: Exchange[];
  selectedExchange: Exchange;
  setSelectedExchange: React.Dispatch<React.SetStateAction<Exchange>>;
  tradedVolumePast24h: number | undefined;
  availableBaseBalance: number;
  availableQuoteBalance: number;
  isActive: boolean | undefined;
  canSubscribe: boolean;
  activeBotForm: string | undefined;
  setActiveBotForm: React.Dispatch<React.SetStateAction<string | undefined>>;
  activeAsset: string | boolean | undefined;
  setActiveAsset: React.Dispatch<
    React.SetStateAction<string | boolean | undefined>
  >;
  stableBalance: string;
  setStableBalance: (value: string) => void;
  assetBalance: string;
  setAssetBalance: (value: string) => void;
  selectedBasket: string;
  setSelectedBasket: (value: string) => void;
  basketOptions: string[];
  shouldCreateNewBasket: boolean;
  success: boolean | undefined;
  setSuccess: (value: boolean | undefined) => void;
  error: string | undefined;
  setError: (value: string | undefined) => void;
};

const Context = createContext<BotItemContext>({
  possibleQuoteAssets: [],
  selectedQuoteAsset: '',
  handleSelectedQuoteAsset: () => null,
  possibleExchanges: [],
  selectedExchange: Exchange.Binance,
  setSelectedExchange: () => null,
  tradedVolumePast24h: undefined,
  availableBaseBalance: 0,
  availableQuoteBalance: 0,
  isActive: undefined,
  canSubscribe: false,
  activeBotForm: undefined,
  setActiveBotForm: () => null,
  activeAsset: undefined,
  setActiveAsset: () => null,
  stableBalance: '0',
  setStableBalance: () => null,
  assetBalance: '0',
  setAssetBalance: () => null,
  selectedBasket: 'Main',
  setSelectedBasket: () => null,
  basketOptions: [],
  shouldCreateNewBasket: false,
  success: undefined,
  setSuccess: () => null,
  error: undefined,
  setError: () => null,
});

type BotItemContextProps = PropsWithChildren & {
  baseAsset: string;
  options: GetTopAssetsByVolumeInfo[];
  balances: Record<string, Record<string, number>> | undefined;
  credits: number;
  subscriptions: Subscription[];
  activeBotForm: string | undefined;
  setActiveBotForm: React.Dispatch<React.SetStateAction<string | undefined>>;
};

export const BotItemContext = ({
  children,
  baseAsset,
  options,
  balances,
  credits,
  subscriptions,
  activeBotForm,
  setActiveBotForm,
}: BotItemContextProps) => {
  const [activeAsset, setActiveAsset] = useState<
    string | boolean | undefined
  >();

  const possibleQuoteAssets = useMemo(() => {
    // return ['USDT', 'USDC'];

    return options
      .map((o) => o.quoteAsset)
      .filter((o, i, s) => s.findIndex((so) => so === o) === i);
  }, [options]);

  const [selectedQuoteAsset, setSelectedQuoteAsset] = useState(
    possibleQuoteAssets[0],
  );

  const possibleExchanges = useMemo(() => {
    // return ['Binance', 'KuCoin', 'Mexc'];

    return options
      .filter((o) => o.quoteAsset === selectedQuoteAsset)
      .map((o) => o.exchange)
      .filter((o, i, s) => s.findIndex((so) => so === o) === i);
  }, [options, selectedQuoteAsset]);

  const [selectedExchange, setSelectedExchange] = useState(
    possibleExchanges[0],
  );

  const basketOptions = useSelector(selectBaskets);
  const [selectedBasket, setSelectedBasket] = useState('Main');
  const [shouldCreateNewBasket, setShouldCreateNewBasket] = useState(false);

  const [stableBalance, setStableBalance] = useState('0');
  const [assetBalance, setAssetBalance] = useState('0');
  const [success, setSuccess] = useState<boolean | undefined>();
  const [error, setError] = useState<string | undefined>();

  // Change exchange if previously selected exchange is not available
  const handleSelectedQuoteAsset = useCallback(
    (quoteAsset: string) => {
      setSelectedQuoteAsset(quoteAsset);

      const exchanges = options
        .filter((o) => o.quoteAsset === quoteAsset)
        .map((o) => o.exchange);

      if (!exchanges.includes(selectedExchange)) {
        setSelectedExchange(exchanges[0]);
      }
    },
    [options, selectedExchange],
  );

  const handleSelectedBasketChange = useCallback((value: string) => {
    if (value === 'Create new...') {
      setShouldCreateNewBasket(true);
      setSelectedBasket('');
      return;
    }

    setSelectedBasket(value);
  }, []);

  const tradedVolumePast24h = useMemo(() => {
    return options.find(
      (o) =>
        o.quoteAsset === selectedQuoteAsset && o.exchange === selectedExchange,
    )?.quoteVolume;
  }, [options, selectedExchange, selectedQuoteAsset]);

  const availableQuoteBalance = useMemo(() => {
    if (!balances) {
      return 0;
    }

    return balances[selectedExchange]?.[selectedQuoteAsset] ?? 0;
  }, [balances, selectedExchange, selectedQuoteAsset]);

  const availableBaseBalance = useMemo(() => {
    if (!balances) {
      return 0;
    }

    return balances[selectedExchange]?.[baseAsset] ?? 0;
  }, [balances, selectedExchange, baseAsset]);

  const isActive = useMemo(() => {
    return options.find(
      (o) =>
        o.quoteAsset === selectedQuoteAsset && o.exchange === selectedExchange,
    )?.isActive;
  }, [options, selectedExchange, selectedQuoteAsset]);

  const isAlreadySubscribed = !!subscriptions.find(
    (s) =>
      s.baseAsset === baseAsset &&
      s.quoteAsset === selectedQuoteAsset &&
      s.exchange === selectedExchange,
  );

  const canSubscribe =
    (isActive ? credits > 0 : credits > 1) && !isAlreadySubscribed;

  const values = useMemo(
    () => ({
      possibleQuoteAssets,
      selectedQuoteAsset,
      handleSelectedQuoteAsset,
      possibleExchanges,
      selectedExchange,
      setSelectedExchange,
      tradedVolumePast24h,
      availableBaseBalance,
      availableQuoteBalance,
      isActive,
      canSubscribe,
      activeBotForm,
      setActiveBotForm,
      activeAsset,
      setActiveAsset,
      stableBalance,
      setStableBalance,
      assetBalance,
      setAssetBalance,
      selectedBasket,
      setSelectedBasket: handleSelectedBasketChange,
      basketOptions,
      shouldCreateNewBasket,
      success,
      setSuccess,
      error,
      setError,
    }),
    [
      activeAsset,
      activeBotForm,
      assetBalance,
      availableBaseBalance,
      availableQuoteBalance,
      canSubscribe,
      error,
      handleSelectedQuoteAsset,
      isActive,
      possibleExchanges,
      possibleQuoteAssets,
      selectedExchange,
      selectedQuoteAsset,
      setActiveBotForm,
      stableBalance,
      success,
      tradedVolumePast24h,
      selectedBasket,
      handleSelectedBasketChange,
      basketOptions,
      shouldCreateNewBasket,
    ],
  );

  return <Context.Provider value={values}>{children}</Context.Provider>;
};

export const useBotItemContext = () => useContext(Context);
